import { z } from 'zod';
/**
 * Steps 3 and 4 — the AML declaration and the legal acceptance (PLAN.md §13,
 * §16, §27.3).
 *
 * Step 3 renders a form the funnel does not own: the CRM's
 * `GET /rest/documents/config/{22|38|65}` returns the fields, and which of the
 * three configs applies is decided by country. That means the shape below is a
 * *description of a form*, not a fixed set of fields — adding a question in the
 * CRM must not require a deploy here.
 *
 * The CRM's own config format is not what travels to the browser. It is a flat
 * map of `field_key → { label, type, meta, choices? }` whose labels contain raw
 * `</span>` fragments left over from a WYSIWYG editor, and whose `meta.set`
 * names the CRM user column the answer writes back to. The server normalises
 * all of that (§27.3): labels are sanitised, `meta.set` never leaves the API,
 * and the three-value type vocabulary is resolved into the control to render.
 */
/**
 * The whole vocabulary, after normalisation.
 *
 * The CRM has three types, not three controls: a `text` field carrying
 * `choices` is a picker rather than an input, and nothing in the CRM's OpenAPI
 * spec says so. Resolving that server-side is what keeps the renderer from
 * having to know the quirk.
 */
export const FORM_FIELD_TYPES = ['text', 'select', 'checkbox'];
export const FormFieldType = z.enum(FORM_FIELD_TYPES);
export const FormField = z.object({
    /** The CRM's own field key. Echoed back verbatim on submit. */
    key: z.string().min(1).max(256),
    /** Already sanitised: plain text, never interpolated as HTML. */
    label: z.string().min(1),
    type: FormFieldType,
    /** Present exactly when `type` is `select`. */
    choices: z.array(z.string()).optional(),
    required: z.boolean(),
});
export const AmlFormResponse = z.object({
    /** Which CRM config this is (22 international, 38 Thailand, 65 Taiwan). */
    configId: z.number().int().positive(),
    title: z.string(),
    /** In CRM order: the declarations were written to be read in sequence. */
    fields: z.array(FormField),
});
/**
 * One answer. A checkbox is a boolean and everything else is a string, which
 * the server re-checks against the config it served rather than trusting the
 * client to have sent the right kind of value for the field.
 */
export const AmlValue = z.union([z.boolean(), z.string().max(512)]);
export const AmlSubmitRequest = z.object({
    /**
     * Keyed by `FormField.key`. Unknown keys are rejected rather than ignored:
     * this payload becomes a compliance document, and quietly dropping a field
     * the server did not recognise is how a declaration ends up filed with a
     * question nobody answered.
     */
    values: z.record(z.string().max(256), AmlValue),
});
export const AmlSubmitResponse = z.object({
    step: z.number().int().min(1).max(4),
});
// ── step 4 — legal acceptance ────────────────────────────────────────────────
/**
 * A company document the user has to accept.
 *
 * The CRM holds no URL for these — `CompanyDocumentDefinition` carries a name
 * and an accept text and nothing to link to — so the screen shows the text and
 * points at the site's own legal pages for the full wording.
 */
export const LegalDocument = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    /** Sanitised like the AML labels: the CRM stores this as editor HTML. */
    acceptText: z.string().optional(),
    /** The version being accepted, when the CRM tracks one. Kept for the audit
     * trail: "accepted the terms" means nothing without saying which terms. */
    version: z.number().int().optional(),
});
export const LegalDocsResponse = z.object({
    documents: z.array(LegalDocument),
});
export const CompleteRequest = z.object({
    /**
     * Echoed back so acceptance is explicit per document rather than a single
     * "I agree" standing in for a list the user never saw. The server checks it
     * against what the CRM actually requires and refuses a partial set.
     */
    acceptedDocumentIds: z.array(z.number().int().positive()).max(50),
});
export const CompleteResponse = z.object({
    /**
     * The CRM's `direct_login` URL, valid for 10 minutes and single-use.
     *
     * This is the one place the funnel is allowed to mint one. In the legacy
     * funnel `step5.php` would hand a direct-login URL to anyone who posted a
     * live application's email address, which made it an account-takeover
     * primitive (finding #2); here it is issued only to the session that owns the
     * application and only after it is complete.
     */
    redirectUrl: z.string().url(),
});
//# sourceMappingURL=compliance.js.map